/* eslint-disable no-nested-ternary */
import PropTypes from "prop-types";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useState, useEffect } from "react";
import jsCookie from "js-cookie";
import {
  format,
  parseISO,
  addMonths,
  isWithinInterval,
  isFuture,
  compareDesc,
  toDate,
} from "date-fns";
import useUserAuth from "helpers/hooks/useUserAuth";
import { getTravelersString, isGroup } from "helpers/formatting";
import Button from "solar/components/Button";
import Title from "components/shared/stays/Title";
import Carousel from "components/shared/stays/Carousel";
import Message from "components/shared/stays/Message";
import AuthModal from "components/auth/AuthModal";
import Anchor from "solar/components/Anchor";
import SectionRhythm from "components/shared/stays/SectionRhythm";
import AvailabilityFixedVariant from "components/stay/AvailabilityFixedVariant";
import AvailabilityFooter from "components/stay/AvailabilityFooter";
import ProductsModal from "components/stay/ProductsModal";
import styles from "components/stay/AvailabilityFixed.module.scss";

export default function AvailabilityFixed({
  products,
  tripID,
  tripName,
  tripTypeDescription,
  guideID,
  guidePrimaryEmployeeFirstName,
  guidePrimaryEmployeeImageUrl,
  responseTime = null,
  closestTrips = [],
}) {
  const visibleProducts = products.filter((p) => p.is_visible);

  const [activeProduct, setActiveProduct] = useState("");
  const [showProductsModal, setShowProductsModal] = useState(false);
  const handleProductsModalClose = () => setShowProductsModal(false);
  function handleProductsModalOpen(p) {
    setShowProductsModal(true);
    setActiveProduct(p);
  }

  const authenticated = useUserAuth();
  const [showAuth, setShowAuth] = useState(false);
  const handleOpenAuth = () => setShowAuth(true);
  const handleCloseAuth = () => setShowAuth(false);

  const ChooseMonth = dynamic(import("components/shared/stays/ChooseMonth"));
  const [showMonth, setShowMonth] = useState(false);
  const handleOpenMonth = () => setShowMonth(true);
  const handleCloseMonth = () => setShowMonth(false);

  const ChooseTravelers = dynamic(
    import("components/shared/stays/ChooseTravelers")
  );
  const [showTravelers, setShowTravelers] = useState(false);
  const handleOpenTravelers = () => setShowTravelers(true);
  const handleCloseTravelers = () => setShowTravelers(false);

  const [hasModifiedTravelers, setHasModifiedTravelers] = useState(false);
  const [numTravelers, setNumTravelers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [month, setMonth] = useState(null);

  useEffect(() => {
    if (jsCookie.get("month")) {
      setMonth(toDate(parseInt(jsCookie.get("month"), 10)));
    }
    if (jsCookie.get("num-adults")) {
      setNumTravelers({
        adults: parseInt(jsCookie.get("num-adults"), 10),
        children: parseInt(jsCookie.get("num-children"), 10),
        infants: parseInt(jsCookie.get("num-infants"), 10),
      });
    }
  }, []);

  function handleSetNumTravelers(obj) {
    setNumTravelers(obj);
    jsCookie.set("num-adults", obj.adults, { expires: 7 });
    jsCookie.set("num-children", obj.children, { expires: 7 });
    jsCookie.set("num-infants", obj.infants, { expires: 7 });
    setHasModifiedTravelers(true);
  }

  function handleSetMonth(val) {
    setMonth(val);
    if (val) {
      jsCookie.set("month", val.getTime(), { expires: 7 });
    } else {
      jsCookie.remove("month");
    }
  }

  const filteredVariants = visibleProducts
    .flatMap((product) => product.variants)
    .filter((v) =>
      month
        ? isWithinInterval(parseISO(v.usage_date), {
            start: month,
            end: addMonths(month, 1),
          })
        : isFuture(parseISO(v.usage_date))
    )
    .sort((a, b) =>
      compareDesc(parseISO(b.usage_date), parseISO(a.usage_date))
    );

  function getNumPayingTravelers() {
    if (numTravelers) return numTravelers.adults + numTravelers.children;
    return 0;
  }

  function getCount(variant) {
    if (isGroup(variant.product)) {
      return "Private group";
    }

    const str = variant.product.total_quantity
      ? `${variant.quantity_available} / ${variant.product.total_quantity} available`
      : "";

    return str;
  }

  function addTravelersStatus(variant) {
    const available = true;
    const count = getCount(variant);
    const action = (
      <Button size={1} onClick={handleOpenTravelers}>
        Add travelers
      </Button>
    );
    return [available, count, action];
  }

  function fullyBookedStatus(variant) {
    const available = false;
    const count = isGroup(variant.product) ? "Booked" : "Fully booked";
    const action = authenticated ? (
      <Message
        tripID={tripID}
        tripName={tripName}
        guideID={guideID}
        guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
        guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
        closestTrips={closestTrips}
        responseTime={responseTime}
        key={tripID}
      >
        <Button size={1} ghost>
          Join waitlist
        </Button>
      </Message>
    ) : (
      <Button size={1} ghost onClick={handleOpenAuth}>
        Join waitlist
      </Button>
    );
    return [available, count, action];
  }

  function bookableStatus(variant) {
    const available = true;
    const count = getCount(variant);
    const action = authenticated ? (
      <Link
        href={`/book/stays/request?trip_id=${tripID}&start_date=${
          variant.usage_date
        }&num_adults=${numTravelers?.adults}&num_children=${
          numTravelers?.children
        }&num_infants=${
          numTravelers?.infants
        }&products[0][quantity]=${getNumPayingTravelers()}&products[0][id]=${
          variant.product.id
        }`}
        legacyBehavior
      >
        <div>
          <Button size={1}>Request</Button>
        </div>
      </Link>
    ) : (
      <Button size={1} onClick={handleOpenAuth}>
        Request
      </Button>
    );
    return [available, count, action];
  }

  function getStatus(variant) {
    if (variant.quantity_available >= getNumPayingTravelers()) {
      if (hasModifiedTravelers) {
        return bookableStatus(variant);
      }
      return addTravelersStatus(variant);
    }
    return fullyBookedStatus(variant);
  }

  function getVariantStatus(variant) {
    const [available, count, action] = getStatus(variant);
    return {
      available,
      count,
      action,
    };
  }

  function privateGroupQuantity(allVisibleProducts) {
    const mainProduct = allVisibleProducts.filter(
      (p) => p.product_type === "main"
    )[0];
    if (
      mainProduct &&
      mainProduct.min_guests &&
      mainProduct.max_guests &&
      mainProduct.min_guests === mainProduct.max_guests
    )
      return mainProduct.min_guests;
    return null;
  }

  const filters = (
    <div className={styles.filters}>
      <Button size={1} ghost onClick={handleOpenMonth}>
        {month ? format(month, "MMMM yyyy") : "Add dates"}
      </Button>
      <Button size={1} ghost onClick={handleOpenTravelers}>
        {getTravelersString(numTravelers)}
      </Button>
      {!privateGroupQuantity(visibleProducts) && (
        <Message
          tripID={tripID}
          tripName={tripName}
          guideID={guideID}
          guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
          guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
          closestTrips={closestTrips}
          responseTime={responseTime}
          key={tripID}
        >
          <Button size={1} ghost>
            Book a private group
          </Button>
        </Message>
      )}
      <Message
        tripID={tripID}
        tripName={tripName}
        guideID={guideID}
        guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
        guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
        closestTrips={closestTrips}
        responseTime={responseTime}
        key={tripID}
      >
        <Button size={1} ghost>
          Create a custom trip
        </Button>
      </Message>
    </div>
  );

  return (
    <>
      <div className={styles.component}>
        <Anchor id="availability" offset />
        <div className={styles.container}>
          <div className={styles.header}>
            <Title>Choose your departure</Title>
          </div>
        </div>
        <SectionRhythm>
          <div className={styles.carousel}>
            <Carousel slidePerLg={4} filters={filters}>
              {filteredVariants.map((variant) => (
                <AvailabilityFixedVariant
                  key={variant.id}
                  price={variant.price}
                  usageDate={variant.usage_date}
                  minNightStay={variant.min_night_stay}
                  productId={variant.product.id}
                  productName={variant.product.name}
                  productPricingType={variant.product.pricing_type}
                  status={getVariantStatus(variant)}
                  handleProductsModalOpen={handleProductsModalOpen}
                />
              ))}
            </Carousel>
          </div>
        </SectionRhythm>
        <SectionRhythm>
          <div className={styles.container}>
            <AvailabilityFooter
              tripID={tripID}
              tripName={tripName}
              guideID={guideID}
              guidePrimaryEmployeeFirstName={guidePrimaryEmployeeFirstName}
              guidePrimaryEmployeeImageUrl={guidePrimaryEmployeeImageUrl}
              closestTrips={closestTrips}
            />
          </div>
        </SectionRhythm>
      </div>
      <ProductsModal
        visibleProducts={visibleProducts}
        showModal={showProductsModal}
        handleModalClose={handleProductsModalClose}
        activeProduct={activeProduct}
        setActiveProduct={setActiveProduct}
        tripTypeDescription={tripTypeDescription}
      />
      {!authenticated && (
        <AuthModal show={showAuth} handleClose={handleCloseAuth} />
      )}
      <ChooseMonth
        show={showMonth}
        handleClose={handleCloseMonth}
        setParentMonth={handleSetMonth}
        month={month}
      />
      <ChooseTravelers
        show={showTravelers}
        travelers={numTravelers}
        handleClose={handleCloseTravelers}
        setNumTravelers={handleSetNumTravelers}
        privateGroupQuantity={privateGroupQuantity(visibleProducts)}
      />
    </>
  );
}

AvailabilityFixed.propTypes = {
  tripID: PropTypes.string.isRequired,
  tripName: PropTypes.string.isRequired,
  tripTypeDescription: PropTypes.string.isRequired,
  guideID: PropTypes.string.isRequired,
  guidePrimaryEmployeeFirstName: PropTypes.string.isRequired,
  guidePrimaryEmployeeImageUrl: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          price: PropTypes.string.isRequired,
          usage_date: PropTypes.string.isRequired,
          min_night_stay: PropTypes.number.isRequired,
          quantity_available: PropTypes.number.isRequired,
          products: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              name: PropTypes.string.isRequired,
              pricing_type: PropTypes.string.isRequired,
            })
          ),
        })
      ),
    })
  ).isRequired,
  responseTime: PropTypes.number,
  closestTrips: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      country: PropTypes.string,
      lowest_price: PropTypes.node,
      product_type: PropTypes.string,
      trip_type_description: PropTypes.string,
      is_featured: PropTypes.bool,
      image_url: PropTypes.string,
      distance: PropTypes.number,
    })
  ),
};
